import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { 
  Box, 
  Button, 
  FormControl, 
  InputLabel, 
  MenuItem, 
  Select, 
  Typography, 
  Paper, 
  Alert, 
  CircularProgress, 
  Divider, 
  Card, 
  CardContent,
  TextField,
  InputAdornment
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import { useAuth0 } from '@auth0/auth0-react';
import config from '../config';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import useCustomerMetadata from '../hooks/useCustomerMetadata';
import { useCustomers } from '../contexts/CustomersContext';
import StatusIndicator from './ui/StatusIndicator';
import LanguageSelector from './ui/LanguageSelector';
import languageData from '../config/languages.json'; // Import languages.json file

const BroadcastPage = () => {
  const [selectedDevice, setSelectedDevice] = useState('');
  const [devices, setDevices] = useState([]);
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [chunkCount, setChunkCount] = useState(0);
  const [transcription, setTranscription] = useState('');
  const [fullTranscript, setFullTranscript] = useState('');
  const [transcripts, setTranscripts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("en-US");
  const [wsState, setWsState] = useState('disconnected');
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [reconnectLog, setReconnectLog] = useState([]);
  const [pingLog, setPingLog] = useState([]);
  const [sentChunks, setSentChunks] = useState(0);
  const [bufferedChunks, setBufferedChunks] = useState(0);
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectMessage, setReconnectMessage] = useState('');
  // New state variables for buffer monitoring
  const [rollingBufferSize, setRollingBufferSize] = useState(0);
  const [postReconnectBufferSize, setPostReconnectBufferSize] = useState(0);
  const [preDisconnectionBufferSize, setPreDisconnectionBufferSize] = useState(0); // Add state for tracking pre-disconnection buffer size
  const [mergedChunksCount, setMergedChunksCount] = useState(0);
  const [autoScroll, setAutoScroll] = useState(true); // State to control auto-scrolling behavior

  // Use refs to store the clientId, WebSocket instance, and session metadata
  const clientIdRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectingRef = useRef(false);
  const isBroadcastingRef = useRef(false);
  const heartbeatIntervalRef = useRef(null);
  const pongTimeoutRef = useRef(null);
  const lastPongRef = useRef(Date.now());
  const audioBufferRef = useRef([]);
  const lastSentChunkIdRef = useRef(0);
  // New refs for enhanced buffering
  const rollingBufferRef = useRef([]); // Keeps last 5 chunks before disconnection
  const postReconnectBufferRef = useRef([]); // Collects 5 chunks after reconnection
  const preDisconnectionBufferRef = useRef([]); // Snapshot of rolling buffer at disconnection time
  const disconnectionTimestampRef = useRef(null);
  const reconnectionTimestampRef = useRef(null);
  const isReconstructingAudioRef = useRef(false);
  const hasDisconnectedRef = useRef(false); // Flag to track if we've already handled a disconnection in this cycle
  const sessionMetadataRef = useRef({
    selectedLanguage: "en-US",
    userOrg: null,
    isBroadcasting: false
  });
  const backoffDelayRef = useRef(1000); // Initial backoff delay
  const processedChunkIdsRef = useRef(new Set()); // New ref to track chunks for which we've received transcripts
  const isResendingBufferedChunksRef = useRef(false); // New ref to track if we're currently resending chunks
  const isMergeSendingInProgressRef = useRef(false); // New ref to track if merge is actively sending chunks
  const lastTranscriptionRef = useRef(null); // New ref to track the last transcription text
  const transcriptionBoxRef = useRef(null); // Ref for the transcription box for auto-scrolling

  // Constants
  const HEARTBEAT_INTERVAL = 10000; // 10 seconds
  const PONG_TIMEOUT = 10000; // 10 seconds
  const RECONNECT_TIMEOUT = 10000; // 10 seconds
  const ROLLING_BUFFER_SIZE = 100; // Rolling buffer size for most recent audio chunks

  const {
    isAuthenticated,
    user,
    isLoading: isAuthLoading,
  } = useAuth0();
  const {
    isActiveSubscription,
    isSubscriber,
    isLoading: isSubscriptionLoading,
    error: subscriptionError
  } = useSubscriptionStatus();
  
  // Function to check if user is at the bottom of the transcription container
  const isAtBottom = () => {
    if (!transcriptionBoxRef.current) return true;
    
    const { scrollTop, scrollHeight, clientHeight } = transcriptionBoxRef.current;
    // Consider "at bottom" if within 20px of the actual bottom
    return scrollHeight - scrollTop - clientHeight < 20;
  };

  // Handle scroll events in the transcription box
  const handleTranscriptionScroll = () => {
    // If user scrolls to bottom, enable auto-scroll
    // If user scrolls away from bottom, disable auto-scroll
    setAutoScroll(isAtBottom());
  };

  // Effect to handle auto-scrolling of transcription box
  useEffect(() => {
    if (autoScroll && transcriptionBoxRef.current && fullTranscript.trim() !== '') {
      // Use smooth scrolling behavior instead of instant jump
      transcriptionBoxRef.current.scrollTo({
        top: transcriptionBoxRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [fullTranscript, autoScroll]);
  const { metadata, isLoading: isMetadataLoading } = useCustomerMetadata();
  const { getUserOrganization, isLoading: isCustomersLoading } = useCustomers();

  // Memoize the organization so that the dependency array stays stable.
  const userOrg = useMemo(() => getUserOrganization(), [getUserOrganization]);

  useEffect(() => {
    if (subscriptionError) {
      console.error('Subscription status error:', subscriptionError);
    }
  }, [subscriptionError]);

  // Load audio devices
  const loadDevices = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const deviceList = await navigator.mediaDevices.enumerateDevices();
      const audioDevices = deviceList.filter((device) => device.kind === 'audioinput');
      setDevices(audioDevices);
      if (audioDevices.length > 0 && !selectedDevice) {
        setSelectedDevice(audioDevices[0].deviceId);
      }
    } catch (error) {
      console.error('Error accessing microphone:', error);
    }
  };

  useEffect(() => {
    loadDevices();
  }, []);

  useEffect(() => {
    navigator.mediaDevices.addEventListener('devicechange', loadDevices);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', loadDevices);
    };
  }, []);

  useEffect(() => {
    if (user) {
      console.log('Auth0 user data loaded:', user);
    }
  }, [user]);

  // Update session metadata when language changes
  useEffect(() => {
    sessionMetadataRef.current.selectedLanguage = selectedLanguage;
    
    // If connected, send the language update
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "set_input_language",
        language: selectedLanguage,
      }));
    }
  }, [selectedLanguage]);

  // Update session metadata when organization changes
  useEffect(() => {
    sessionMetadataRef.current.userOrg = userOrg;
    
    // If organization changes, reconnect WebSocket
    if (userOrg && wsRef.current) {
      console.log('BroadcastPage: Organization changed, reconnecting WebSocket');
      wsRef.current.close(1000, "Organization changed");
      connectWebSocket();
    }
  }, [userOrg]);

  // Update session metadata when broadcasting state changes
  useEffect(() => {
    isBroadcastingRef.current = isBroadcasting;
    sessionMetadataRef.current.isBroadcasting = isBroadcasting;
  }, [isBroadcasting]);

  // WebSocket connection and reconnection logic
  const connectWebSocket = useCallback(() => {
    if (reconnectingRef.current) {
      console.log('Already attempting to reconnect, skipping this attempt');
      return;
    }

    // Clear any existing reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    reconnectingRef.current = true;
    setReconnecting(true);
    
    // Create a client ID that identifies this as a broadcaster
    // Use the user's organization ID or sub if available, otherwise generate a random ID
    const orgId = userOrg?.id ? String(userOrg.id) : 'unknown-org';
    const userId = user?.id ? String(user.id) : Date.now().toString(36);
    clientIdRef.current = `${orgId}-broadcaster-${Date.now().toString(36)}`;
    console.log(`Connecting to WebSocket as broadcaster: ${clientIdRef.current}`);
    
    console.log(`Connecting to WebSocket: ${config.getWebSocketUrl(clientIdRef.current)}`);
    
    try {
      const ws = new WebSocket(config.getWebSocketUrl(clientIdRef.current));
      wsRef.current = ws;
      
      // Set a connection timeout
      const connectionTimeoutId = setTimeout(() => {
        console.log('WebSocket connection timeout');
        if (ws.readyState !== WebSocket.OPEN) {
          ws.close(1000, "Connection timeout");
          // Force reconnect since onclose might not be called
          reconnectingRef.current = false;
          scheduleReconnect();
        }
      }, 5000); // 5 second timeout
      
      ws.onopen = () => {
        console.log('WebSocket connection established');
        clearTimeout(connectionTimeoutId);
        setWsState('connected');
        setReconnecting(false);
        reconnectingRef.current = false;
        setReconnectAttempt(0);
        setReconnectMessage('');
        
        // Reset the backoff delay on successful connection
        backoffDelayRef.current = 1000;
        
        // If this is a reconnection after a disconnection, record the timestamp
        if (disconnectionTimestampRef.current) {
          reconnectionTimestampRef.current = Date.now();
          console.log(`Reconnected after ${((reconnectionTimestampRef.current - disconnectionTimestampRef.current) / 1000).toFixed(3)}s disconnection`);
          
          // Reset post-reconnect buffer to collect new chunks
          postReconnectBufferRef.current = [];
          setPostReconnectBufferSize(0);
          
          // Don't reset hasDisconnectedRef here yet - it will be reset after the merge is complete
        }
        
        // Restore session metadata if available
        if (sessionMetadataRef.current.selectedLanguage) {
          console.log(`Restoring source language: ${sessionMetadataRef.current.selectedLanguage}`);
          ws.send(JSON.stringify({
            type: 'set_input_language',
            language: sessionMetadataRef.current.selectedLanguage
          }));
        }
        
        // Start heartbeat
        startHeartbeat();
        
        // If we have been disconnected and there are pre-disconnection chunks,
        // we need to merge and send them immediately
        if (hasDisconnectedRef.current && preDisconnectionBufferRef.current.length > 0) {
          console.log(`Initiating audio merge process after reconnection with ${preDisconnectionBufferRef.current.length} pre-disconnection chunks`);
          
          // Set flag to postpone any buffer resending until after merge
          isResendingBufferedChunksRef.current = true;
          
          // Don't wait for post-reconnection chunks, start merging immediately
          setTimeout(() => {
            mergeAudioAcrossDisconnection();
          }, 500); // Small delay to ensure connection is stable
        }
        // If we have buffered chunks but no pre-disconnection chunks
        else if (hasDisconnectedRef.current && audioBufferRef.current.length > 0) {
          console.log(`No pre-disconnection chunks, but found ${audioBufferRef.current.length} buffered chunks to send`);
          
          // Resend buffered audio chunks
          setTimeout(() => {
            resendBufferedAudioChunks();
          }, 500);
        }
      };
      
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        // Handle pong messages
        if (data.type === 'pong') {
          if (pongTimeoutRef.current) {
            clearTimeout(pongTimeoutRef.current);
            pongTimeoutRef.current = null;
          }
          return;
        }
        
        // Handle transcription messages
        if (data.type === 'transcription') {
          // Update UI with transcription
          setTranscription(data.text);
          
          // Add to full transcript with deduplication check
          const newText = data.text.trim();
          const lastText = lastTranscriptionRef.current?.trim() || '';
          
          // Check if this is a duplicate transcription
          if (lastText && lastText === newText) {
            console.log(`Detected duplicate transcription: "${newText}". Skipping addition to full transcript.`);
          } else if (lastText && newText.startsWith(lastText)) {
            // Incremental transcription - only add the new part
            const incrementalText = newText.substring(lastText.length);
            console.log(`Incremental transcription detected. Adding only new part: "${incrementalText}"`);
            setFullTranscript((prev) => prev + incrementalText);
          } else {
            // New transcription - add with a space
            setFullTranscript((prev) => prev + ' ' + newText);
          }
          
          // Store the last transcription text for comparison
          lastTranscriptionRef.current = newText;
          
          // Update transcripts list
          setTranscripts(prev => [data, ...prev].slice(0, 10));
          
          // Log received transcription for debugging
          console.log(`Received transcription for text: "${data.text.substring(0, 50)}${data.text.length > 50 ? '...' : ''}"`);
          
          // If the message contains a chunkId, add it to the processed chunks set
          if (data.chunkId !== undefined && data.chunkId !== null) {
            // Ensure chunk ID is stored consistently as a number
            const chunkId = typeof data.chunkId === 'string' ? parseInt(data.chunkId, 10) : data.chunkId;
            
            // Track this chunk as processed
            if (!processedChunkIdsRef.current.has(chunkId)) {
              processedChunkIdsRef.current.add(chunkId);
              console.log(`Added chunk ID ${chunkId} to processed chunks set (from transcription)`);
              
              // Log the current processed chunks for debugging
              if (processedChunkIdsRef.current.size <= 20) { // Only log if not too many
                console.log(`[PROCESSED CHUNKS] Current set: [${Array.from(processedChunkIdsRef.current).join(', ')}]`);
              } else {
                console.log(`[PROCESSED CHUNKS] Current set has ${processedChunkIdsRef.current.size} chunks (too many to display)`);
              }
            } else {
              console.log(`Chunk ID ${chunkId} was already marked as processed`);
            }
            
            // If we have a pre-disconnection buffer, remove this chunk from it
            if (hasDisconnectedRef.current && preDisconnectionBufferRef.current.length > 0) {
              const initialLength = preDisconnectionBufferRef.current.length;
              preDisconnectionBufferRef.current = preDisconnectionBufferRef.current.filter(chunk => {
                // Normalize chunk IDs to numbers for comparison
                const bufferChunkId = typeof chunk.chunkId === 'string' ? 
                  parseInt(chunk.chunkId, 10) : chunk.chunkId;
                return bufferChunkId !== chunkId;
              });
              
              // Update the state to reflect the new buffer size
              setPreDisconnectionBufferSize(preDisconnectionBufferRef.current.length);
              
              // If we actually removed something, log it
              if (initialLength > preDisconnectionBufferRef.current.length) {
                console.log(`Auto-removed processed chunk ${chunkId} from pre-disconnection buffer`);
                console.log(`Pre-disconnection buffer now has ${preDisconnectionBufferRef.current.length} chunks`);
              }
            }
          } else {
            console.warn(`Received transcription without chunkId: "${data.text.substring(0, 30)}..."`);
            
            // If chunkId is missing, we need to estimate which chunks were processed
            // Use a heuristic approach: mark the oldest unprocessed chunks as processed
            const recentChunks = [...rollingBufferRef.current].sort((a, b) => a.chunkId - b.chunkId);
            
            if (recentChunks.length > 0) {
              // Get the oldest unprocessed chunk ID
              const oldestUnprocessedIds = recentChunks
                .filter(chunk => !processedChunkIdsRef.current.has(chunk.chunkId))
                .slice(0, 2) // Assume up to 2 chunks might be processed per transcription (reduced from 3 to be more conservative)
                .map(chunk => chunk.chunkId);
              
              if (oldestUnprocessedIds.length > 0) {
                console.log(`No chunkId provided. Marking oldest unprocessed chunks as processed: [${oldestUnprocessedIds.join(', ')}]`);
                
                // Mark these chunks as processed
                oldestUnprocessedIds.forEach(id => {
                  processedChunkIdsRef.current.add(id);
                });
                
                // Update pre-disconnection buffer if needed
                if (hasDisconnectedRef.current && preDisconnectionBufferRef.current.length > 0) {
                  const initialLength = preDisconnectionBufferRef.current.length;
                  preDisconnectionBufferRef.current = preDisconnectionBufferRef.current.filter(chunk => {
                    const bufferChunkId = typeof chunk.chunkId === 'string' ? 
                      parseInt(chunk.chunkId, 10) : chunk.chunkId;
                    return !oldestUnprocessedIds.includes(bufferChunkId);
                  });
                  
                  setPreDisconnectionBufferSize(preDisconnectionBufferRef.current.length);
                  
                  if (initialLength > preDisconnectionBufferRef.current.length) {
                    console.log(`Auto-removed estimated processed chunks from pre-disconnection buffer`);
                    console.log(`Pre-disconnection buffer now has ${preDisconnectionBufferRef.current.length} chunks`);
                  }
                }
              }
            }
          }
          
          return;
        }
      };
      
      ws.onclose = (event) => {
        if (wsRef.current === ws) {
          setWsState('disconnected');
          console.log(`WebSocket connection closed: ${event.code} ${event.reason}`);
          
          // Clean up WebSocket resources
          clearTimeout(connectionTimeoutId);
          
          // Clear heartbeat interval
          if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
          }
          
          // Clear pong timeout
          if (pongTimeoutRef.current) {
            clearTimeout(pongTimeoutRef.current);
            pongTimeoutRef.current = null;
          }
          
          // Clear the merge lock in case it was active during disconnection
          isMergeSendingInProgressRef.current = false;
          
          // Only record first disconnection event
          if (!hasDisconnectedRef.current) {
            // Set disconnection flag
            hasDisconnectedRef.current = true;
            
            // Record disconnection timestamp
            const now = new Date();
            disconnectionTimestampRef.current = now.getTime();
            console.log(`First disconnection recorded at ${now.toISOString()}`);
            
            // Take a snapshot of the rolling buffer to preserve pre-disconnection chunks
            const rollingBufferCopy = [...rollingBufferRef.current];
            
            // Filter out chunks that have already been processed
            const unprocessedChunks = rollingBufferCopy.filter(chunk => {
              // Normalize the chunk ID to a number
              const chunkId = typeof chunk.chunkId === 'string' ? parseInt(chunk.chunkId, 10) : chunk.chunkId;
              
              // Check if this chunk has been processed (received transcription)
              const isProcessed = processedChunkIdsRef.current.has(chunkId);
              
              if (isProcessed) {
                console.log(`Skipping already processed chunk ${chunkId} from pre-disconnection buffer`);
              }
              
              return !isProcessed;
            });
            
            // Add all unprocessed chunks to the pre-disconnection buffer
            preDisconnectionBufferRef.current = unprocessedChunks;
            setPreDisconnectionBufferSize(preDisconnectionBufferRef.current.length);
            console.log(`Captured ${preDisconnectionBufferRef.current.length} unprocessed chunks in variable-sized pre-disconnection buffer`);
            if (preDisconnectionBufferRef.current.length > 0) {
              console.log(`Pre-disconnection buffer chunk IDs: [${preDisconnectionBufferRef.current.map(chunk => chunk.chunkId).join(', ')}]`);
            }
          } else {
            // For subsequent disconnections, preserve the existing pre-disconnection buffer
            console.log(`Subsequent disconnection event at ${new Date().toISOString()}, preserving existing pre-disconnection buffer`);
            
            // We should add any new unprocessed chunks from the rolling buffer
            // that aren't already in the pre-disconnection buffer
            const rollingBufferCopy = [...rollingBufferRef.current];
            const preDisconnectionChunkIds = new Set(preDisconnectionBufferRef.current.map(
              chunk => typeof chunk.chunkId === 'string' ? parseInt(chunk.chunkId, 10) : chunk.chunkId
            ));
            
            // Filter for chunks that are not in the pre-disconnection buffer and not processed
            const additionalUnprocessedChunks = rollingBufferCopy.filter(chunk => {
              // Normalize the chunk ID to a number
              const chunkId = typeof chunk.chunkId === 'string' ? parseInt(chunk.chunkId, 10) : chunk.chunkId;
              
              // Check if this chunk is already in the pre-disconnection buffer
              const isInPreDisconnectionBuffer = preDisconnectionChunkIds.has(chunkId);
              
              // Check if this chunk has been processed (received transcription)
              const isProcessed = processedChunkIdsRef.current.has(chunkId);
              
              return !isInPreDisconnectionBuffer && !isProcessed;
            });
            
            // Add any new unprocessed chunks to the pre-disconnection buffer
            if (additionalUnprocessedChunks.length > 0) {
              preDisconnectionBufferRef.current = [...preDisconnectionBufferRef.current, ...additionalUnprocessedChunks];
              setPreDisconnectionBufferSize(preDisconnectionBufferRef.current.length);
              console.log(`Added ${additionalUnprocessedChunks.length} new unprocessed chunks to pre-disconnection buffer`);
              console.log(`Pre-disconnection buffer now has ${preDisconnectionBufferRef.current.length} chunks`);
              console.log(`Pre-disconnection buffer chunk IDs: [${preDisconnectionBufferRef.current.map(chunk => chunk.chunkId).join(', ')}]`);
            }
          }
          
          scheduleReconnect();
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        
        // Clear the merge lock in case of error
        isMergeSendingInProgressRef.current = false;
        
        // Force reconnect in case onclose doesn't get called
        reconnectingRef.current = false;
        scheduleReconnect();
      };
      
      return ws;
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      reconnectingRef.current = false;
      scheduleReconnect();
      return null;
    }
  }, [userOrg, user]);

  const scheduleReconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    
    // Add jitter to prevent all clients from reconnecting simultaneously
    const jitter = Math.random() * 1000;
    const delay = backoffDelayRef.current + jitter;
    
    console.log(`Scheduling reconnect in ${Math.round(delay / 1000)} seconds`);
    setReconnectMessage(`Reconnecting in ${Math.round(delay / 1000)} seconds...`);
    setReconnectLog(prev => [...prev, `Reconnecting in ${Math.round(delay / 1000)} seconds...`]);
    
    reconnectTimeoutRef.current = setTimeout(() => {
      // Increase backoff for next attempt (capped at MAX_BACKOFF_DELAY)
      backoffDelayRef.current = Math.min(backoffDelayRef.current * 2, 30000);
      
      setReconnectAttempt(prev => prev + 1);
      reconnectingRef.current = false; // Reset the reconnecting flag
      connectWebSocket();
    }, delay);
  }, [connectWebSocket]);

  const startHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    
    heartbeatIntervalRef.current = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        console.log('Sending heartbeat ping');
        wsRef.current.send(JSON.stringify({
          type: 'ping',
          timestamp: Date.now()
        }));
        
        // Set a timeout to check for pong response
        if (pongTimeoutRef.current) {
          clearTimeout(pongTimeoutRef.current);
        }
        
        pongTimeoutRef.current = setTimeout(() => {
          const timeSinceLastPong = Date.now() - lastPongRef.current;
          console.log(`No pong received in ${timeSinceLastPong}ms`);
          
          if (timeSinceLastPong > 10000) { // 10 seconds
            console.log('No pong received, closing connection to trigger reconnect');
            if (wsRef.current) {
              wsRef.current.close();
              // Force reconnect immediately instead of waiting for onclose
              scheduleReconnect();
            }
          }
        }, 5000); // Wait 5 seconds for pong before considering connection dead
      }
    }, 10000); // Send ping every 10 seconds
  }, [scheduleReconnect]);

  // Send an audio chunk to the WebSocket server
  const sendAudioChunk = useCallback((audioBlob, chunkId) => {
    // Skip if we're currently in the middle of merging audio
    if (isReconstructingAudioRef.current || isMergeSendingInProgressRef.current) {
      console.log(`Skipping audio chunk ${chunkId} as we're currently merging audio`);
      return;
    }
    
    // Check if this chunk has already been processed
    const normalizedChunkId = typeof chunkId === 'string' ? parseInt(chunkId, 10) : chunkId;
    if (processedChunkIdsRef.current.has(normalizedChunkId)) {
      console.log(`Skipping already processed chunk ${normalizedChunkId}`);
      return;
    }
    
    // Create message with audio data and chunk ID - using the format expected by the backend
    const message = {
      type: 'audio',
      data: audioBlob,
      recordingLengthMs: 1000, // Default length
      chunkId: normalizedChunkId,
      userId: user?.id || 'unknown',
      sourceLanguage: selectedLanguage
    };
    
    // Add to rolling buffer for pre-disconnection capture
    if (rollingBufferRef.current.length >= ROLLING_BUFFER_SIZE) {
      rollingBufferRef.current.shift(); // Remove oldest chunk
    }
    rollingBufferRef.current.push(message);
    setRollingBufferSize(rollingBufferRef.current.length);
    
    console.log(`Rolling buffer contains chunks: [${rollingBufferRef.current.map(c => c.chunkId).join(', ')}]`);
    
    // If we're collecting post-reconnection chunks
    if (reconnectionTimestampRef.current && 
        postReconnectBufferRef.current.length < ROLLING_BUFFER_SIZE && 
        !isReconstructingAudioRef.current) {
      postReconnectBufferRef.current.push(message);
      setPostReconnectBufferSize(postReconnectBufferRef.current.length);
      
      const bufferIds = postReconnectBufferRef.current.map(c => c.chunkId);
      console.log(`Post-reconnection buffer contains chunks: [${bufferIds.join(', ')}]`);
    }
    
    // If WebSocket is open, send the chunk
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && !isMergeSendingInProgressRef.current) {
      wsRef.current.send(JSON.stringify(message));
      setSentChunks(prev => prev + 1);
      console.log(`Sent audio chunk ${chunkId}`);
      
      // DO NOT mark as processed yet - only mark when we receive transcription response
      // This ensures we don't lose chunks that were sent but not processed by the server
    } else {
      // If WebSocket is closed, add to buffer for later resending
      audioBufferRef.current.push(message);
      setBufferedChunks(audioBufferRef.current.length);
      console.log(`Buffered audio chunk ${chunkId} due to closed connection`);
    }
  }, [user, selectedLanguage]);

  // Resend buffered audio chunks when connection is restored
  const resendBufferedAudioChunks = useCallback(() => {
    if (audioBufferRef.current.length === 0) {
      console.log('No buffered chunks to resend');
      return;
    }
    
    console.log(`Resending ${audioBufferRef.current.length} buffered audio chunks`);
    
    // Create a copy of the buffer and clear the original
    const chunksToSend = [...audioBufferRef.current];
    audioBufferRef.current = [];
    
    // Sort chunks by ID to ensure they're sent in order
    chunksToSend.sort((a, b) => a.chunkId - b.chunkId);
    
    // Function to send chunks with a delay between them
    const sendChunkWithDelay = (index) => {
      if (index >= chunksToSend.length) {
        console.log('Finished resending buffered chunks');
        setBufferedChunks(0);
        isResendingBufferedChunksRef.current = false;
        return;
      }
      
      const chunk = chunksToSend[index];
      
      // Only send if WebSocket is still open
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        try {
          wsRef.current.send(JSON.stringify(chunk));
          setSentChunks(prev => prev + 1);
          console.log(`Resent buffered audio chunk ${chunk.chunkId}`);
          
          // Schedule the next chunk with a delay
          setTimeout(() => sendChunkWithDelay(index + 1), 50);
        } catch (error) {
          console.error('Error resending buffered chunk:', error);
          // Put remaining chunks back in the buffer
          audioBufferRef.current = [...audioBufferRef.current, ...chunksToSend.slice(index)];
          setBufferedChunks(audioBufferRef.current.length);
          console.log(`Re-buffered ${chunksToSend.length - index} chunks due to send error`);
        }
      } else {
        // Put remaining chunks back in the buffer
        audioBufferRef.current = [...audioBufferRef.current, ...chunksToSend.slice(index)];
        setBufferedChunks(audioBufferRef.current.length);
        console.log(`Re-buffered ${chunksToSend.length - index} chunks due to closed connection`);
      }
    };
    
    // Start sending chunks
    sendChunkWithDelay(0);
  }, []);

  // Merge audio chunks from before and after disconnection to ensure a continuous audio experience
  const mergeAudioAcrossDisconnection = useCallback(() => {
    // Prevent concurrent merges
    if (isReconstructingAudioRef.current) {
      console.log('Already reconstructing audio, skipping this merge request');
      return;
    }
    
    isReconstructingAudioRef.current = true;
    
    try {
      console.log('==============================================================');
      console.log('STARTING AUDIO MERGE PROCESS');
      console.log('==============================================================');
      
      // Set lock to prevent new audio chunks from being sent during the merge
      isMergeSendingInProgressRef.current = true;
      
      // Log the current processed chunks for debugging
      console.log(`[PROCESSED CHUNKS] Currently tracking ${processedChunkIdsRef.current.size} processed chunk IDs`);
      if (processedChunkIdsRef.current.size > 0) {
        console.log(`[PROCESSED CHUNKS] Processed chunk IDs: [${Array.from(processedChunkIdsRef.current).join(', ')}]`);
      }
      
      // Calculate and log disconnection duration for debugging
      const disconnectionDuration = reconnectionTimestampRef.current - disconnectionTimestampRef.current;
      console.log(`Disconnection lasted ${(disconnectionDuration / 1000).toFixed(2)}s`);
      
      // Log the current processed chunks again for visibility
      console.log(`[PROCESSED CHUNKS] Currently tracking ${processedChunkIdsRef.current.size} processed chunk IDs`);
      if (processedChunkIdsRef.current.size > 0) {
        console.log(`[PROCESSED CHUNKS] Processed chunk IDs: [${Array.from(processedChunkIdsRef.current).join(', ')}]`);
      }
      
      // ===================================================================
      // [STEP 1] Gather audio chunks from all sources (snapshots, accumulated buffers, etc.)
      // ===================================================================
      
      // 1.1: Get pre-disconnection chunks from the snapshot buffer
      console.log('[MERGE STEP 1] Starting with variable-sized pre-disconnection buffer containing unprocessed chunks');
      if (preDisconnectionBufferRef.current.length === 0) {
        console.log('[MERGE STEP 1] Pre-disconnection buffer is empty, no unprocessed chunks to merge');
      } else {
        console.log(`[MERGE STEP 1] Pre-disconnection buffer has ${preDisconnectionBufferRef.current.length} unprocessed chunks`);
        console.log(`[MERGE STEP 1] Pre-disconnection chunk IDs: [${preDisconnectionBufferRef.current.map(chunk => chunk.chunkId).join(', ')}]`);
      }
      
      // 1.2: Get main buffer chunks that were collected during disconnection
      const mainBufferChunks = [...audioBufferRef.current];
      console.log(`[MERGE STEP 1] Found ${mainBufferChunks.length} chunks in main buffer collected during disconnection`);
      
      if (mainBufferChunks.length > 0) {
        console.log(`[MERGE STEP 1] Main buffer chunk IDs: [${mainBufferChunks.map(chunk => chunk.chunkId).join(', ')}]`);
      } else {
        console.log(`[MERGE STEP 1] No main buffer chunks available`);
      }
      
      // 1.3: Get post-reconnection chunks if any
      const postReconnectChunks = [...postReconnectBufferRef.current];
      console.log(`[MERGE STEP 1] Found ${postReconnectChunks.length} post-reconnection chunks`);
      
      if (postReconnectChunks.length > 0) {
        console.log(`[MERGE STEP 1] Post-reconnection chunk IDs: [${postReconnectChunks.map(chunk => chunk.chunkId).join(', ')}]`);
      } else {
        console.log(`[MERGE STEP 1] No post-reconnection chunks available`);
      }
      
      // =======================================================
      // [STEP 2] Filter out chunks that have already been processed
      // =======================================================
      
      // 2.1: Filter pre-disconnection chunks
      const filteredPreDisconnectionChunks = preDisconnectionBufferRef.current.filter(chunk => {
        // Normalize the chunk ID to a number
        const chunkId = typeof chunk.chunkId === 'string' ? parseInt(chunk.chunkId, 10) : chunk.chunkId;
        
        // Check if this chunk has already been processed
        const isProcessed = processedChunkIdsRef.current.has(chunkId);
        
        if (isProcessed) {
          console.log(`[MERGE STEP 2] Skipping pre-disconnection chunk ${chunkId} as it has already been processed`);
        } else {
          console.log(`[MERGE STEP 2] Keeping unprocessed pre-disconnection chunk ${chunkId}`);
        }
        
        return !isProcessed;
      });
      console.log(`[MERGE STEP 2] After filtering, ${filteredPreDisconnectionChunks.length} unprocessed pre-disconnection chunks remain`);
      
      // 2.2: Filter main buffer chunks - we generally don't mark these as processed until sent
      const filteredMainBufferChunks = mainBufferChunks.filter(chunk => {
        // Normalize the chunk ID to a number
        const chunkId = typeof chunk.chunkId === 'string' ? parseInt(chunk.chunkId, 10) : chunk.chunkId;
        
        // Check if this chunk has already been processed
        const isProcessed = processedChunkIdsRef.current.has(chunkId);
        
        if (isProcessed) {
          console.log(`[MERGE STEP 2] Skipping main buffer chunk ${chunkId} as it has already been processed`);
        } else {
          console.log(`[MERGE STEP 2] Keeping unprocessed main buffer chunk ${chunkId}`);
        }
        
        return !isProcessed;
      });
      console.log(`[MERGE STEP 2] After filtering, ${filteredMainBufferChunks.length} unprocessed main buffer chunks remain`);
      
      // 2.3: Filter post-reconnection chunks
      const filteredPostReconnectChunks = postReconnectChunks.filter(chunk => {
        // Normalize the chunk ID to a number
        const chunkId = typeof chunk.chunkId === 'string' ? parseInt(chunk.chunkId, 10) : chunk.chunkId;
        
        // Check if this chunk has already been processed
        const isProcessed = processedChunkIdsRef.current.has(chunkId);
        
        if (isProcessed) {
          console.log(`[MERGE STEP 2] Skipping post-reconnection chunk ${chunkId} as it has already been processed`);
        } else {
          console.log(`[MERGE STEP 2] Keeping unprocessed post-reconnection chunk ${chunkId}`);
        }
        
        return !isProcessed;
      });
      console.log(`[MERGE STEP 2] After filtering, ${filteredPostReconnectChunks.length} unprocessed post-reconnection chunks remain`);
      
      // =================================================================
      // [STEP 3] Normalize chunk IDs to numbers for consistent comparison
      // =================================================================
      
      // Helper function to normalize chunk IDs to numbers
      const normalizeChunkId = (chunk) => {
        if (typeof chunk.chunkId === 'string') {
          chunk.chunkId = parseInt(chunk.chunkId, 10);
        }
      };
      
      // 3.1: Normalize all chunk IDs
      filteredPreDisconnectionChunks.forEach(normalizeChunkId);
      filteredMainBufferChunks.forEach(normalizeChunkId);
      filteredPostReconnectChunks.forEach(normalizeChunkId);
      
      console.log(`[MERGE STEP 3] Normalized all chunk IDs to numeric values for consistent ordering`);
      
      // ==================================================
      // [STEP 4] Combine all chunks from different sources
      // ==================================================
      
      // 4.1: Combine chunks from all sources
      let mergedChunks = [
        ...filteredPreDisconnectionChunks,
        ...filteredMainBufferChunks,
        ...filteredPostReconnectChunks
      ];
      console.log(`[MERGE STEP 4] Initial merge contains ${mergedChunks.length} total unprocessed chunks from all sources`);
      console.log(`[MERGE STEP 4] Merged chunk IDs before sorting: [${mergedChunks.map(c => c.chunkId).join(', ')}]`);
      
      // 4.2: Sort by chunk ID to ensure proper order
      mergedChunks.sort((a, b) => {
        const chunkIdA = typeof a.chunkId === 'string' ? parseInt(a.chunkId, 10) : a.chunkId;
        const chunkIdB = typeof b.chunkId === 'string' ? parseInt(b.chunkId, 10) : b.chunkId;
        return chunkIdA - chunkIdB;
      });
      
      // 4.3: Deduplicate chunks by ID using more strict comparison
      const uniqueChunks = [];
      const seenIds = new Set();
      
      // First pass: remove exact duplicates
      for (const chunk of mergedChunks) {
        const chunkId = typeof chunk.chunkId === 'string' ? parseInt(chunk.chunkId, 10) : chunk.chunkId;
        
        // Skip chunks that have already been processed
        if (processedChunkIdsRef.current.has(chunkId)) {
          console.log(`[MERGE STEP 4] Skipping already processed chunk with ID ${chunkId}`);
          continue;
        }
        
        // Skip duplicate chunks
        if (seenIds.has(chunkId)) {
          console.log(`[MERGE STEP 4] Removing duplicate chunk with ID ${chunkId}`);
          continue;
        }
        
        // Add this chunk to our unique set
        seenIds.add(chunkId);
        uniqueChunks.push({...chunk, chunkId: chunkId}); // Normalize ID to number
      }
      
      console.log(`[MERGE STEP 4] After deduplication, ${uniqueChunks.length} chunks remain`);
      console.log(`[MERGE STEP 4] Sorted merged chunk IDs: [${uniqueChunks.map(c => c.chunkId).join(', ')}]`);
      
      // [STEP 5] Sort chunks by ID for proper sequencing
      // ===============================================
      
      // 5.1: Sort chunks by ID
      uniqueChunks.sort((a, b) => a.chunkId - b.chunkId);
      
      console.log(`[MERGE STEP 5] Final merge contains ${uniqueChunks.length} unique, unprocessed chunks in chronological order`);
      console.log(`[MERGE STEP 5] Final unique chunk IDs: [${uniqueChunks.map(chunk => chunk.chunkId).join(', ')}]`);
      
      // [STEP 6] Send chunks to server
      // =============================
      
      // Only proceed with sending if we have chunks to send and WebSocket is open
      if (uniqueChunks.length > 0 && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        console.log(`[MERGE STEP 6] WebSocket is open, sending ${uniqueChunks.length} merged chunks`);
        
        // Send each chunk with a small delay to avoid overwhelming the server
        const sendChunksWithDelay = (chunkIndex = 0) => {
          if (chunkIndex >= uniqueChunks.length) {
            console.log(`[MERGE STEP 6] Finished sending all ${uniqueChunks.length} merged chunks`);
            console.log('------------------------------------------------------------');
            console.log('AUDIO MERGE COMPLETED SUCCESSFULLY');
            console.log('------------------------------------------------------------');
            
            // Clear the audio buffer since we've sent everything
            audioBufferRef.current = [];
            setBufferedChunks(0);
            
            console.log(`All merged chunks sent successfully. No need to resend buffered chunks as they were included in the merge.`);
            
            // Release locks to allow normal audio sending to resume
            isMergeSendingInProgressRef.current = false;
            
            // Final cleanup
            cleanupAfterMerge();
            return;
          }
          
          const chunk = uniqueChunks[chunkIndex];
          
          // Send the chunk
          wsRef.current.send(JSON.stringify(chunk));
          console.log(`[MERGE STEP 6] Sending merged chunk ${chunkIndex + 1}/${uniqueChunks.length} with ID ${chunk.chunkId}`);
          
          // Schedule next chunk with a small delay
          setTimeout(() => {
            sendChunksWithDelay(chunkIndex + 1);
          }, 100); // 100ms delay between chunks
        };
        
        // Start sending chunks
        sendChunksWithDelay();
      } else {
        console.log(`[MERGE STEP 6] WebSocket not open or no chunks to send, adding ${uniqueChunks.length} chunks to buffer`);
        
        // Add all chunks to the main buffer for later sending
        audioBufferRef.current = [...audioBufferRef.current, ...uniqueChunks];
        setBufferedChunks(audioBufferRef.current.length);
        
        // Release the merge lock to allow normal sending to resume
        isMergeSendingInProgressRef.current = false;
        
        // Final cleanup
        cleanupAfterMerge();
      }
      
      // Helper function for final cleanup
      function cleanupAfterMerge() {
        // [STEP 7] Final cleanup
        // ======================
        
        console.log('[MERGE STEP 7] Cleaning up state for next disconnection cycle');
        
        // 7.1: Reset disconnection timestamp
        disconnectionTimestampRef.current = null;
        console.log('[MERGE STEP 7] Reset disconnection timestamp');
        
        // 7.2: Clear post-reconnection buffer
        postReconnectBufferRef.current = [];
        setPostReconnectBufferSize(0);
        console.log('[MERGE STEP 7] Cleared post-reconnection buffer');
        
        // 7.3: Clear pre-disconnection buffer to prevent reuse
        preDisconnectionBufferRef.current = [];
        console.log('[MERGE STEP 7] Cleared pre-disconnection buffer after merge');
        setPreDisconnectionBufferSize(0);
        
        // 7.4: Reset disconnection flag to prepare for next disconnection cycle
        hasDisconnectedRef.current = false;
        console.log('[MERGE STEP 7] Reset disconnection flag, ready for next disconnection cycle');
        
        // 7.5: Release the reconstruction lock
        isReconstructingAudioRef.current = false;
        
        console.log('==============================================================');
        console.log('AUDIO MERGE COMPLETED');
        console.log('==============================================================');
      }
    } catch (error) {
      console.error('Error during audio merge:', error);
      
      // Always release locks when errors occur to avoid deadlocks
      isMergeSendingInProgressRef.current = false;
      isReconstructingAudioRef.current = false;
      
      // Reset state even on error to avoid getting stuck
      hasDisconnectedRef.current = false;
      disconnectionTimestampRef.current = null;
      reconnectionTimestampRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && userOrg && !wsRef.current) {
      connectWebSocket();
    }
    
    // Cleanup on unmount or when dependencies change
    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000, "Component unmounting");
        wsRef.current = null;
      }
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      // Stop heartbeat
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  }, [isAuthenticated, userOrg, connectWebSocket]);

  const startBroadcasting = async () => {
    try {
      if (!userOrg) {
        console.error('No organization found for user');
        return;
      }
      console.log('BroadcastPage: Starting broadcast for organization:', userOrg.displayName);
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: selectedDevice ? { exact: selectedDevice } : undefined,
          sampleRate: 48000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      const audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 48000 });
      const mediaStreamSource = audioContext.createMediaStreamSource(stream);

      let currentChunk = 0;
      let workletNode;

      try {
        await audioContext.audioWorklet.addModule('/audio-processor.js');
        workletNode = new AudioWorkletNode(audioContext, 'audio-recorder-processor');

        workletNode.port.onmessage = (event) => {
          if (event.data.type === 'audio-data') {
            const audioData = event.data.data;
            const chunkId = lastSentChunkIdRef.current++;
            sendAudioChunk(audioData, chunkId);
            setChunkCount(++currentChunk);
          }
        };

        mediaStreamSource.connect(workletNode);
        workletNode.connect(audioContext.destination);

        setMediaRecorder({ stream, audioContext, workletNode });
        setIsBroadcasting(true);
        sessionMetadataRef.current.isBroadcasting = true;
        
        // Ensure we have a WebSocket connection
        if (wsState !== 'connected' && wsState !== 'reconnecting') {
          connectWebSocket();
        }
      } catch (error) {
        console.error('Error setting up audio worklet:', error);
        stream.getTracks().forEach((track) => track.stop());
        throw error;
      }
    } catch (error) {
      console.error('Error starting broadcast:', error);
    }
  };

  const stopBroadcasting = () => {
    if (mediaRecorder) {
      console.log('BroadcastPage: Stopping broadcast');
      mediaRecorder.stream.getTracks().forEach((track) => track.stop());
      mediaRecorder.workletNode.disconnect();
      mediaRecorder.audioContext.close();
      setMediaRecorder(null);
      setIsBroadcasting(false);
      sessionMetadataRef.current.isBroadcasting = false;
      setChunkCount(0);
      
      // Clear audio buffer
      audioBufferRef.current = [];
      setBufferedChunks(0);
      
      // Reset WebSocket connection to ensure language change takes effect
      if (wsRef.current) {
        console.log('Resetting WebSocket connection for fresh language selection');
        wsRef.current.close(1000, "Broadcasting stopped, reset for language selection");
        wsRef.current = null;
        setWsState('disconnected'); // Update the WebSocket state to reflect the closed connection
      }
    }
  };

  const handleUpgradeClick = async () => {
    setIsLoading(true);
    try {
      if (!user || !user.email) {
        throw new Error('User data not available. Please try logging in again.');
      }
      console.log('Starting checkout process...', { email: user.email, name: user.name });
      const response = await fetch(`${config.API_URL}/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email, name: user.name || '' }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Server error response:', errorData);
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      console.log('Checkout response:', data);
      if (!data.url) {
        throw new Error(data.error || 'No checkout URL received');
      }
      window.location.assign(data.url);
    } catch (error) {
      console.error('Checkout error:', error);
      alert(error.message || 'Failed to start checkout process');
    } finally {
      setIsLoading(false);
    }
  };

  const renderBroadcastButton = () => {
    if (!userOrg) {
      return (
        <Button variant="contained" color="primary" onClick={() => window.location.href = '/account'}>
          Add an Organization Name
        </Button>
      );
    }
    return (
      <Button
        variant="contained"
        color="primary"
        onClick={isBroadcasting ? stopBroadcasting : startBroadcasting}
        disabled={!selectedDevice || isLoading || wsState === 'connecting' || wsState === 'reconnecting'}
      >
        {isBroadcasting ? 'Stop Broadcasting' : 'Start Broadcasting'}
      </Button>
    );
  };

  const renderBroadcastControls = () => {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        gap: 2,
        mt: 3,
        mb: 2,
        maxWidth: '600px',
        mx: 'auto',
        width: '100%'
      }}>
        <Button
          variant="contained"
          color={isBroadcasting ? "error" : "primary"}
          onClick={isBroadcasting ? stopBroadcasting : startBroadcasting}
          disabled={isLoading || reconnecting || !selectedDevice}
          startIcon={isBroadcasting ? <StopIcon /> : <MicIcon />}
          sx={{ px: 4, py: 1.5, borderRadius: 2 }}
          fullWidth
        >
          {isBroadcasting ? "Stop Broadcasting" : "Start Broadcasting"}
        </Button>
        
        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <CircularProgress size={24} sx={{ mr: 1 }} />
            <Typography variant="body2">Initializing...</Typography>
          </Box>
        )}
        
        {reconnectMessage && (
          <Alert severity="warning" sx={{ width: '100%' }}>
            {reconnectMessage}
          </Alert>
        )}
      </Box>
    );
  };

  if (isAuthLoading || isSubscriptionLoading || isMetadataLoading || isCustomersLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Please log in to access the broadcast feature.</Alert>
      </Box>
    );
  }

  if (subscriptionError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Error checking subscription status: {subscriptionError}
        </Alert>
        <Typography variant="body1" gutterBottom>
          We're having trouble verifying your subscription status. This might happen if:
        </Typography>
        <ul>
          <li>Your organization name hasn't been set up yet</li>
          <li>There's a temporary issue with our subscription service</li>
          <li>Your account information needs to be refreshed</li>
        </ul>
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" onClick={() => window.location.href = '/account'} sx={{ mr: 2 }}>
            Go to Account Page
          </Button>
          <Button variant="outlined" onClick={() => window.location.reload()}>
            Refresh Page
          </Button>
        </Box>
      </Box>
    );
  }

  if (!isSubscriber) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Subscription Required
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You need an active subscription to access the broadcast feature.
          </Typography>
          <Button variant="contained" onClick={handleUpgradeClick} disabled={isLoading}>
            Subscribe Now
          </Button>
        </Paper>
      </Box>
    );
  }

  // Get organization display name for heading
  const orgDisplayName = userOrg ? userOrg.displayName : 'Your Organization';

  // Render the connection status section to show current WebSocket state
  const renderConnectionStatus = () => {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <StatusIndicator 
          status={
            wsState === 'connected' 
              ? isBroadcasting ? 'broadcasting' : 'connected'
              : wsState === 'reconnecting' ? 'reconnecting' : wsState
          }
          label={
            wsState === 'connected' 
              ? isBroadcasting ? 'Broadcasting' : 'Connected' 
              : wsState === 'connecting' ? 'Connecting...' 
              : wsState === 'reconnecting' ? 'Reconnecting...'
              : 'Disconnected'
          }
        />
      </Box>
    );
  };
  
  // Enhanced version of the existing renderBroadcastControls function
  // with updated styling to match the StreamingPage design

  // Main component render
  return (
    <Box 
      component="main" 
      sx={{ py: 2 }}
      role="main"
      aria-label="Audio broadcasting page"
    >
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          borderRadius: 2,
          maxWidth: '800px',
          mx: 'auto'
        }}
      >
        {/* Main heading with organization name */}
        <Typography 
          variant="h1" 
          component="h1" 
          gutterBottom
          sx={{ 
            mb: 3,
            textAlign: 'center',
            fontWeight: 500,
            fontSize: '2rem'
          }}
        >
          Broadcast as {orgDisplayName}
        </Typography>
        
        {/* Description of the broadcast functionality */}
        <Typography 
          variant="body1" 
          component="p" 
          sx={{ 
            mb: 3, 
            textAlign: 'center',
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          Share your voice with listeners around the world. Your audio will be automatically transcribed and translated for your audience in their preferred language.
        </Typography>
        
        {/* Main controls card */}
        <Card elevation={2}>
          <CardContent>
            {/* Connection status section */}
            {renderConnectionStatus()}
            
            {/* Device and language selection */}
            <Box sx={{ 
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' }, 
              alignItems: { xs: 'center', sm: 'flex-start' }, 
              justifyContent: { xs: 'flex-start', sm: 'space-between' },
              width: '100%',
              gap: 2, 
              mb: 2
            }}>
              <Box sx={{ 
                minWidth: { xs: '100%', sm: '200px' }, 
                maxWidth: { xs: '100%', sm: '300px' },
                display: 'flex',
                flexDirection: 'column',
                width: { xs: '100%', sm: 'auto' }
              }}>
                <FormControl fullWidth>
                  <InputLabel>Microphone</InputLabel>
                  <Select 
                    value={selectedDevice} 
                    label="Microphone" 
                    onChange={(e) => setSelectedDevice(e.target.value)}
                    disabled={isBroadcasting}
                    startAdornment={
                      <InputAdornment position="start">
                        <MicIcon fontSize="small" />
                      </InputAdornment>
                    }
                  >
                    {devices.map((device) => (
                      <MenuItem key={device.deviceId} value={device.deviceId}>
                        {device.label || `Microphone ${device.deviceId}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              
              <Box sx={{ 
                minWidth: { xs: '100%', sm: '200px' }, 
                maxWidth: { xs: '100%', sm: '300px' },
                display: 'flex',
                flexDirection: 'column',
                width: { xs: '100%', sm: 'auto' }
              }}>
                <LanguageSelector 
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  disabled={isBroadcasting}
                  languages={languageData.languages.filter(lang => lang.sttModel && lang.sttModel.trim() !== '')}
                  fullWidth
                />
              </Box>
            </Box>
            
            {/* Broadcast controls */}
            {renderBroadcastControls()}
          </CardContent>
        </Card>
        
        {/* Transcription display card - always visible */}
        <Card elevation={2} sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Transcriptions
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box 
              ref={transcriptionBoxRef}
              onScroll={handleTranscriptionScroll}
              sx={{ 
                height: '300px', 
                overflowY: 'auto',
                p: 2,
                borderRadius: 1,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                fontFamily: 'system-ui',
                fontSize: '1rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              {fullTranscript.trim() === '' ? (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                  {isBroadcasting 
                    ? "Your transcription will appear here as you speak..." 
                    : "Start broadcasting to see your transcription appear here..."}
                </Typography>
              ) : (
                <Typography variant="body1">{fullTranscript}</Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      </Paper>
    </Box>
  );
}

export default BroadcastPage;
